/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    Button,
    EmailField,
    Heading,
    TextField,
    Icon,
    DividerNew,
    type FormValidationAlertErrors,
    FormValidationAlert,
    useFlags,
} from 'sarsaparilla';
import { useFieldRefValidation, getMessageFromError } from 'sarsaparilla/src/utils';

import {
    useRecaptchaWithFallback,
    recaptchaV2ValidationError,
    RecaptchaCheckbox,
} from 'shared-ui/recaptcha';
import { postGuestLogin, postLoginV2, postLoginV2Errors } from 'shared-ui/api/account';
import { TermsAndPrivacyPolicy } from '../components/TermsAndPrivacyPolicy';
import { ProtectedByRecaptcha } from '../components/ProtectedByRecaptcha';
import { LoginFormErrors, type LoginFormErrorType } from './LoginFormErrors';

import { useAuthModalScreen } from '../AuthModalScreenContext';

interface LoginFormProps {
    isGuestAllowed?: boolean;
}

export function LoginForm({ isGuestAllowed }: LoginFormProps) {
    const { closeModal, setScreen, storeUserData } = useAuthModalScreen();

    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [isGuestLoading, setIsGuestLoading] = React.useState(false);
    const [errorType, setErrorType] = React.useState<LoginFormErrorType | null>(null);
    const [validationErrors, setValidationErrors] =
        React.useState<FormValidationAlertErrors | null>(null);
    const { addFieldRef, validate } = useFieldRefValidation();
    const { accountLoginRecaptcha } = useFlags();
    const isRecaptchaEnabled = accountLoginRecaptcha === true;
    const {
        getToken,
        getRecaptchaRegion,
        handleRecaptchaError,
        isChallengeRequired,
        recaptchaCheckboxProps,
    } = useRecaptchaWithFallback();

    const submitForm = async ({
        recaptchaToken,
        isV2,
    }: {
        recaptchaToken?: string | null;
        isV2?: boolean;
    }) => {
        const username = email.trim().toLowerCase();
        const passwordTrimmed = password.trim();

        const { isValid, errors } = validate();
        const isV2Invalid = isRecaptchaEnabled && isV2 && !recaptchaToken;

        if (
            !isValid ||
            !username ||
            !passwordTrimmed ||
            (isRecaptchaEnabled && isV2Invalid)
        ) {
            if (isRecaptchaEnabled && isV2Invalid) {
                errors.push(recaptchaV2ValidationError);
            }
            setValidationErrors(errors);
            return;
        }

        try {
            // Extra check to make sure we have a token value
            if (isRecaptchaEnabled && !recaptchaToken) {
                setErrorType('recaptchaFailed');
                return;
            }

            const { data } = await postLoginV2({
                username,
                password: passwordTrimmed,
                recaptchaToken,
                recaptchaType: isRecaptchaEnabled ? getRecaptchaRegion() : undefined,
            });

            if (data.mfa_login) {
                // transition to MFA code screen
                setScreen('mfaCode');
            } else {
                // User is logged in - store user data in redux and local storage
                storeUserData(data);

                closeModal();
            }
        } catch (error) {
            const { shouldRetry, isFailed } = isRecaptchaEnabled
                ? handleRecaptchaError(error)
                : { shouldRetry: false, isFailed: false };
            const errorMessage = getMessageFromError(error);

            if (shouldRetry) {
                // Score was too low - reveal the v2 checkbox for the next attempt
                setErrorType('additionalChallenge');
            } else if (isFailed) {
                setErrorType('recaptchaFailed');
            } else if (errorMessage === postLoginV2Errors.ERROR) {
                setErrorType('badCredentials');
            } else if (errorMessage === postLoginV2Errors.UNKNOWN_USER) {
                setErrorType('badCredentials');
            } else if (errorMessage === postLoginV2Errors.LOCKOUT) {
                setErrorType('lockout');
            } else {
                setErrorType('unknown');
            }
        }
    };

    const onRecaptchaV2Solve = async (recaptchaToken: string) => {
        setIsLoading(true);
        setValidationErrors(null);
        setErrorType(null);

        try {
            await submitForm({ recaptchaToken, isV2: true });
        } catch {
            // Defensive only - all errors should be handled in submitForm()
            setErrorType('unknown');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setValidationErrors(null);
        setErrorType(null);

        // Get a recaptcha token and call submitForm. The backend sends this through
        // the Enterprise assessment for the “login” action.
        try {
            // V2 Checkbox "additional challenge" case
            if (isRecaptchaEnabled && isChallengeRequired) {
                const recaptchaToken = await getToken('login');

                if (!recaptchaToken) {
                    setValidationErrors([recaptchaV2ValidationError]);
                    return;
                }

                await submitForm({ recaptchaToken, isV2: true });
                return;
            }

            // V3 Score-based challenge case
            const recaptchaToken = isRecaptchaEnabled
                ? await getToken('login')
                : undefined;

            await submitForm({ recaptchaToken });
        } catch {
            setErrorType('unknown');
        } finally {
            setIsLoading(false);
        }
    };

    const continueAsGuest = async () => {
        setIsGuestLoading(true);
        setErrorType(null);
        setValidationErrors(null);

        try {
            const { data } = await postGuestLogin();
            // Guest is logged in, store data and close modal
            storeUserData(data);
            closeModal({ preventRedirect: true });
        } catch (error) {
            setErrorType('unknown');
        } finally {
            setIsGuestLoading(false);
        }
    };

    return (
        <div className="px-sm-4 pt-sm-4 pb-sm-1">
            <Heading
                headingLevel={1}
                appearance="h4"
                className="mb-1 pr-4"
                id="modal-heading"
            >
                Log In to {process.env.SITE_NAME}
            </Heading>

            <div className="pt-half">
                Don‘t have a login yet?{' '}
                <Button
                    appearance="link"
                    iconAfterElement={<Icon iconName="chevron-right" />}
                    className="ml-half"
                    style={{ verticalAlign: 'baseline' }}
                    gaTrackingId="440238972040"
                    onClick={() => {
                        setScreen('createAccount');
                    }}
                    isDisabled={isLoading || isGuestLoading}
                >
                    Create an account
                </Button>
            </div>

            <form className="my-4" onSubmit={onSubmit} noValidate>
                <FormValidationAlert errors={validationErrors} className="mb-4" />

                <LoginFormErrors errorType={errorType} className="mb-4" />

                <EmailField
                    label="Email"
                    id="email"
                    name="username"
                    isRequired
                    autoComplete="username"
                    ref={addFieldRef(0)}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />

                <TextField
                    type="password"
                    autoComplete="current-password"
                    label="Password"
                    id="password"
                    name="password"
                    isRequired
                    enableShowPassword
                    className="mt-2"
                    ref={addFieldRef(1)}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                {isRecaptchaEnabled && (
                    <RecaptchaCheckbox
                        {...recaptchaCheckboxProps}
                        isFallbackOnly
                        action="login"
                        className="mt-3"
                        onChange={onRecaptchaV2Solve}
                    />
                )}

                <Button
                    type="submit"
                    size="lg"
                    shouldFitContainer
                    className="mt-3"
                    isLoading={isLoading}
                    isDisabled={isGuestLoading}
                    gaTrackingId="440238972040"
                >
                    Log In
                </Button>

                <Button
                    type="button"
                    appearance="subtle"
                    shouldFitContainer
                    className="mt-1"
                    gaTrackingId="440238972040"
                    onClick={() => {
                        setScreen('requestPasswordReset');
                    }}
                    isDisabled={isLoading || isGuestLoading}
                >
                    Forgot Your Password?
                </Button>
            </form>

            {isGuestAllowed ? (
                <div className="my-4">
                    <DividerNew className="my-4" />

                    <Heading headingLevel={2} appearance="h6">
                        New to {process.env.SITE_NAME}?
                    </Heading>

                    <div className="mt-1">
                        <Button
                            appearance="link"
                            style={{ verticalAlign: 'baseline' }}
                            isDisabled={isLoading || isGuestLoading}
                            gaTrackingId="440238972040"
                            onClick={() => {
                                setScreen('createAccount');
                            }}
                        >
                            Create an account
                        </Button>
                        , or check out as a guest. You can sign up for an account later if
                        you‘re interested.
                    </div>

                    <Button
                        type="button"
                        appearance="secondary"
                        size="lg"
                        className="mt-2"
                        shouldFitContainer
                        isDisabled={isLoading}
                        isLoading={isGuestLoading}
                        onClick={continueAsGuest}
                        gaTrackingId="440238972040"
                    >
                        Continue as a Guest
                    </Button>
                </div>
            ) : null}

            <DividerNew className="mb-2" />

            <TermsAndPrivacyPolicy />

            {isRecaptchaEnabled && <ProtectedByRecaptcha />}
        </div>
    );
}
